import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Alert, Spinner, Button } from 'flowbite-react';
import { fetchWithAuth } from '../utils/api';
import { getChatHistoryEndpoints } from '../config/api';

interface ChatHistoryItem {
  id: number;
  page_id: string | number;
  sender_id: string | number;
  message: string;
  note?: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
}

const ChatHistoryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState<ChatHistoryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceMessage, setTraceMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [traceData, setTraceData] = useState<any>(null);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          setError('Thiếu ID lịch sử trò chuyện');
          return;
        }

        const endpoint = getChatHistoryEndpoints().DETAIL;
        const response = await fetchWithAuth(`${endpoint}/${id}`);
        if (response.success && response.data) {
          setItem(response.data);
        } else {
          setError('Không thể tải chi tiết lịch sử trò chuyện');
        }
      } catch (err) {
        setError('Đã xảy ra lỗi khi tải dữ liệu');
        console.error('Load chat history detail error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [id]);

  const handleTraceOtherPages = async () => {
    if (!item) return;

    setTraceLoading(true);
    setTraceMessage(null);
    setTraceData(null);

    try {
      const response = await fetchWithAuth(
        `${getChatHistoryEndpoints().TRACE_OTHER_PAGE}?id=${item.id}`
      );

      if (response.success) {
        setTraceMessage({
          type: 'success',
          text: 'Truy vết lịch sử tin nhắn trên trang khác thành công!',
        });
        setTraceData(response.data);
      } else {
        setTraceMessage({
          type: 'failure',
          text: response.error || 'Truy vết thất bại',
        });
      }
    } catch (err) {
      console.error('Trace other pages error:', err);
      setTraceMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi truy vết',
      });
    } finally {
      setTraceLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải chi tiết...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Chi tiết lịch sử trò chuyện
          </h1>
          <p className="text-gray-600">Hiển thị đầy đủ thông tin tin nhắn</p>
        </div>
        <div className="flex items-center gap-2">
          <Button color="gray" onClick={() => navigate('/chat-history')}>
            Quay lại
          </Button>
          <Button
            color="blue"
            onClick={handleTraceOtherPages}
            disabled={traceLoading || !item}
          >
            {traceLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Đang truy vết...
              </>
            ) : (
              'Truy vết lịch sử tin nhắn trên trang khác'
            )}
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="failure" className="mb-4">
          <span>{error}</span>
        </Alert>
      )}

      {traceMessage && (
        <Alert color={traceMessage.type} className="mb-4">
          <span>{traceMessage.text}</span>
        </Alert>
      )}

      {item && (
        <Card>
          <div className="p-2 space-y-4">
            <div>
              <div className="text-sm text-gray-500">ID</div>
              <div className="text-gray-900 font-medium">{item.id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Page ID</div>
              <div className="text-gray-900 font-medium">{item.page_id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Sender ID</div>
              <div className="text-gray-900 font-medium">{item.sender_id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Trạng thái</div>
              <div className="text-gray-900 font-medium">{item.status}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Message</div>
              <div className="text-gray-900 whitespace-pre-wrap break-words">
                {item.message}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Note</div>
              <div className="text-gray-900 whitespace-pre-wrap break-words">
                {item.note ?? '-'}
              </div>
            </div>
            {(item.created_at || item.updated_at) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Ngày tạo</div>
                  <div className="text-gray-900">{item.created_at ?? '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Cập nhật</div>
                  <div className="text-gray-900">{item.updated_at ?? '-'}</div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {traceData && (
        <Card className="mt-6">
          <div className="p-2 space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Kết quả truy vết
            </h3>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto">
              {JSON.stringify(traceData, null, 2)}
            </pre>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ChatHistoryDetail;
